import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { CustomerCollectionViewsInitialState } from '../globalSlices/customerCollectionViewsSlice';
import {
  getCustomerCollectionViews,
  createCustomerCollectionView,
  updateCustomerCollectionView,
  deleteCustomerCollectionView,
} from '../services/customerCollectionViewsService';
import { CustomerCollectionView, CustomerCollectionViewUpdate } from '@dill/dill-shared';

export const getCustomerCollectionViewsExtraReducer = (
  builder: ActionReducerMapBuilder<CustomerCollectionViewsInitialState>
) => {
  const reducerId = 'getCustomerCollectionViews';
  builder.addCase(getCustomerCollectionViews.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getCustomerCollectionViews.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.views = action.payload.data as CustomerCollectionView[];
      }
    }
  );
  builder.addCase(getCustomerCollectionViews.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createCustomerCollectionViewExtraReducer = (
  builder: ActionReducerMapBuilder<CustomerCollectionViewsInitialState>
) => {
  const reducerId = 'createCustomerCollectionView';
  builder.addCase(createCustomerCollectionView.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createCustomerCollectionView.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.views = [...state.views, action.payload.data as CustomerCollectionView];
      }
    }
  );
  builder.addCase(createCustomerCollectionView.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateCustomerCollectionViewExtraReducer = (
  builder: ActionReducerMapBuilder<CustomerCollectionViewsInitialState>
) => {
  const reducerId = 'updateCustomerCollectionView';
  builder.addCase(updateCustomerCollectionView.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    updateCustomerCollectionView.fulfilled,
    (
      state,
      action: PayloadAction<
        (AppServerResponse & { viewId: string; data: CustomerCollectionViewUpdate }) | null
      >
    ) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success) {
        const { viewId, data } = action.payload;
        state.views = state.views.map((view) => (view.id === viewId ? { ...view, ...data } : view));
      }
    }
  );
  builder.addCase(updateCustomerCollectionView.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deleteCustomerCollectionViewExtraReducer = (
  builder: ActionReducerMapBuilder<CustomerCollectionViewsInitialState>
) => {
  const reducerId = 'deleteCustomerCollectionView';
  builder.addCase(deleteCustomerCollectionView.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    deleteCustomerCollectionView.fulfilled,
    (state, action: PayloadAction<(AppServerResponse & { viewId: string }) | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success) {
        const { viewId } = action.payload;
        state.views = state.views.filter((view) => view.id !== viewId);
      }
    }
  );
  builder.addCase(deleteCustomerCollectionView.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};
